// client/src/pages/GarageVehicleDetailPage.jsx
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Edit2, Check, X, Gauge, ChevronRight, Wrench, AlertTriangle,
  Mail, History, Users, UserPlus, Trash2, ClipboardList, Plus, RefreshCw,
} from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useVehicle, useUpdateVehicle, useUpdateMileage, useJobs, useIssues,
  useAddVehicleOwner, useRemoveVehicleOwner,
  useCheckinTemplates, useVehicleCheckinSchedules, useCreateCheckinSchedule,
  useUpdateCheckinSchedule, useDeleteCheckinSchedule, useVehicleCheckins,
} from '../api/hooks'

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

// ─── Constants ────────────────────────────────────────────────────────────────

const FUEL_STYLES = {
  petrol:   'bg-blue-500/15 text-blue-400',
  diesel:   'bg-slate-500/20 text-slate-400',
  electric: 'bg-green-500/15 text-green-400',
  hybrid:   'bg-teal-500/15 text-teal-400',
  other:    'bg-gray-500/15 text-gray-400',
}

const JOB_STATUS_STYLES = {
  requested:         'bg-blue-500/15 text-blue-400',
  quoted:            'bg-purple-500/15 text-purple-400',
  awaiting_approval: 'bg-yellow-500/15 text-yellow-400',
  approved:          'bg-indigo-500/15 text-indigo-400',
  in_progress:       'bg-amber-500/15 text-amber-400',
  waiting_on_parts:  'bg-orange-500/15 text-orange-400',
  completed:         'bg-green-500/15 text-green-400',
  cancelled:         'bg-gray-500/15 text-gray-400',
}

const JOB_STATUS_LABELS = {
  requested: 'Requested', quoted: 'Quoted', awaiting_approval: 'Awaiting',
  approved: 'Approved', in_progress: 'In Progress', waiting_on_parts: 'Waiting on Parts',
  completed: 'Completed', cancelled: 'Cancelled',
}

const ISSUE_STATUS_STYLES = {
  open:        'bg-blue-500/15 text-blue-400',
  in_progress: 'bg-amber-500/15 text-amber-400',
  resolved:    'bg-green-500/15 text-green-400',
  deferred:    'bg-gray-500/15 text-gray-400',
}

const ISSUE_STATUS_LABELS = {
  open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', deferred: 'Deferred',
}

const SEVERITY_DOT = {
  low:      'bg-blue-400',
  medium:   'bg-amber-400',
  high:     'bg-orange-400',
  critical: 'bg-red-400',
}

const FUEL_OPTIONS  = ['petrol', 'diesel', 'electric', 'hybrid', 'other']
const TRANS_OPTIONS = ['manual', 'automatic', 'other']

// ─── Plate ────────────────────────────────────────────────────────────────────

function Plate({ registration, size = 'md' }) {
  const sizes = { sm: 'px-2 py-0.5 text-xs', md: 'px-3 py-1.5 text-sm', lg: 'px-4 py-2 text-lg' }
  return (
    <span className={clsx(
      'inline-block font-mono font-bold tracking-widest bg-yellow-400 text-black rounded',
      sizes[size]
    )}>
      {registration}
    </span>
  )
}

// ─── Detail row ───────────────────────────────────────────────────────────────

function DetailRow({ label, value }) {
  if (!value) return null
  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-[var(--border)] last:border-0">
      <span className="text-xs text-[var(--text-muted)] shrink-0">{label}</span>
      <span className="text-sm text-[var(--text-primary)] text-right">{value}</span>
    </div>
  )
}

// ─── Owners Panel ─────────────────────────────────────────────────────────────

function OwnersPanel({ vehicleId, ownership, isAdmin }) {
  const addOwner    = useAddVehicleOwner()
  const removeOwner = useRemoveVehicleOwner()

  const [showForm, setShowForm] = useState(false)
  const [email,    setEmail]    = useState('')

  const activeOwners = (ownership || []).filter(o => !o.ownedTo)

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!email.trim()) return
    try {
      await addOwner.mutateAsync({ vehicleId, email: email.trim() })
      toast.success('Owner added')
      setEmail('')
      setShowForm(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add owner')
    }
  }

  const handleRemove = async (userId) => {
    try {
      await removeOwner.mutateAsync({ vehicleId, userId })
      toast.success('Owner removed')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove owner')
    }
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-[var(--text-primary)] flex items-center gap-2">
          <Users size={15} className="text-[var(--text-muted)]" />
          Owners
          {activeOwners.length > 0 && (
            <span className="text-[var(--text-muted)] font-normal text-sm">
              ({activeOwners.length})
            </span>
          )}
        </h3>
        {isAdmin && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            <UserPlus size={13} />
            Add owner
          </button>
        )}
      </div>

      {activeOwners.length === 0 && !showForm && (
        <p className="text-sm text-[var(--text-muted)]">No portal users linked to this vehicle.</p>
      )}

      {activeOwners.length > 0 && (
        <div className="space-y-2 mb-3">
          {activeOwners.map(owner => (
            <div
              key={owner.userId}
              className="flex items-center gap-3 p-2.5 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg"
            >
              <div className="w-7 h-7 rounded-full bg-indigo-600/15 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-semibold text-indigo-400">
                  {(owner.userName || owner.userEmail || '?')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                {owner.userName && (
                  <p className="text-sm font-medium text-[var(--text-primary)] truncate">{owner.userName}</p>
                )}
                {owner.userEmail && (
                  <a
                    href={`mailto:${owner.userEmail}`}
                    className="text-xs text-[var(--text-muted)] hover:text-indigo-400 transition-colors truncate block"
                  >
                    <span className="flex items-center gap-1">
                      <Mail size={11} />
                      {owner.userEmail}
                    </span>
                  </a>
                )}
              </div>
              {owner.isPrimary && (
                <span className="text-xs text-[var(--text-muted)] shrink-0">Primary</span>
              )}
              {isAdmin && (
                <button
                  onClick={() => handleRemove(owner.userId)}
                  disabled={removeOwner.isPending}
                  className="p-1 text-[var(--text-muted)] hover:text-red-400 transition-colors disabled:opacity-50 shrink-0"
                  title="Remove owner"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {isAdmin && showForm && (
        <form onSubmit={handleAdd} className="flex items-center gap-2 mt-2">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="User's email address"
            className="flex-1 px-3 py-1.5 text-sm bg-[var(--bg-base)] border border-[var(--border)]
                       rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
                       focus:outline-none focus:border-indigo-500 transition-colors"
            autoFocus
            required
          />
          <button
            type="submit"
            disabled={addOwner.isPending}
            className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                       text-white font-medium rounded-lg transition-colors whitespace-nowrap"
          >
            {addOwner.isPending ? 'Adding…' : 'Add'}
          </button>
          <button
            type="button"
            onClick={() => { setShowForm(false); setEmail('') }}
            className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
          >
            <X size={15} />
          </button>
        </form>
      )}
    </div>
  )
}

// ─── Check-in schedule panel ──────────────────────────────────────────────────

const FREQ_LABELS = { monthly: 'Monthly', quarterly: 'Quarterly', biannual: 'Biannual', annual: 'Annual' }
const FREQ_STYLES = {
  monthly:   'bg-indigo-500/15 text-indigo-400',
  quarterly: 'bg-purple-500/15 text-purple-400',
  biannual:  'bg-amber-500/15 text-amber-400',
  annual:    'bg-rose-500/15 text-rose-400',
}

function AddScheduleModal({ vehicleId, templates, onClose }) {
  const create = useCreateCheckinSchedule()
  const today  = new Date().toISOString().slice(0, 10)
  const [templateId, setTemplateId] = useState('')
  const [nextDueAt,  setNextDueAt]  = useState(today)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!templateId) return toast.error('Please select a template')
    try {
      await create.mutateAsync({ vehicleId, templateId, nextDueAt })
      toast.success('Check-in schedule created')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create schedule')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Add Check-In Schedule</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Template</label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)}
              className={inputCls} required>
              <option value="">Select a template…</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">First due date</label>
            <input type="date" value={nextDueAt} onChange={e => setNextDueAt(e.target.value)}
              className={inputCls} required />
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            A check-in reminder email will be sent to the vehicle owner on or after this date.
            After each submission the next due date advances automatically.
          </p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={create.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {create.isPending ? 'Creating…' : 'Create Schedule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function CheckinSchedulePanel({ vehicleId, isAdmin }) {
  const { data: schedules  = [], isLoading: schLoading  } = useVehicleCheckinSchedules(vehicleId)
  const { data: checkins   = [], isLoading: chkLoading  } = useVehicleCheckins(vehicleId)
  const { data: templates  = []                          } = useCheckinTemplates()
  const updateSchedule = useUpdateCheckinSchedule()
  const deleteSchedule = useDeleteCheckinSchedule()
  const [showAdd, setShowAdd] = useState(false)

  const activeTemplates = templates.filter(t => t.is_active)

  const handleToggle = async (s) => {
    try {
      await updateSchedule.mutateAsync({ id: s.id, isActive: !s.is_active })
    } catch {
      toast.error('Failed to update')
    }
  }

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete the ${s.template_name} schedule?`)) return
    try {
      await deleteSchedule.mutateAsync({ id: s.id, vehicleId })
      toast.success('Schedule deleted')
    } catch {
      toast.error('Failed to delete')
    }
  }

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
        <h3 className="font-medium text-[var(--text-primary)] flex items-center gap-2">
          <ClipboardList size={15} className="text-[var(--text-muted)]" />
          Check-In Schedules
          {!schLoading && schedules.length > 0 && (
            <span className="text-[var(--text-muted)] font-normal text-sm">({schedules.length})</span>
          )}
        </h3>
        {isAdmin && (
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            <Plus size={13} />
            Add schedule
          </button>
        )}
      </div>

      {schLoading ? (
        <div className="p-4 space-y-2">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="h-10 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : schedules.length === 0 ? (
        <p className="px-5 py-4 text-sm text-[var(--text-muted)]">
          No check-in schedules set up. Add one to send periodic health check reminders to the vehicle owner.
        </p>
      ) : (
        <div className="divide-y divide-[var(--border)]">
          {schedules.map(s => {
            const recentCheckins = checkins.filter(c => c.schedule_id === s.id)
            const lastCheckin    = recentCheckins[0]
            const isOverdue      = s.is_active && new Date(s.next_due_at) < new Date()

            return (
              <div key={s.id} className="flex items-center gap-3 px-5 py-3.5">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={clsx('text-sm font-medium', !s.is_active && 'text-[var(--text-muted)] line-through')}>
                      {s.template_name}
                    </span>
                    <span className={clsx('px-2 py-0.5 text-xs font-medium rounded',
                      FREQ_STYLES[s.frequency] || 'bg-gray-500/15 text-gray-400')}>
                      {FREQ_LABELS[s.frequency] || s.frequency}
                    </span>
                    {!s.is_active && (
                      <span className="px-2 py-0.5 text-xs bg-gray-500/15 text-gray-400 rounded">Paused</span>
                    )}
                    {isOverdue && (
                      <span className="px-2 py-0.5 text-xs bg-red-500/15 text-red-400 rounded">Overdue</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-xs text-[var(--text-muted)]">
                      Due: {new Date(s.next_due_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </p>
                    {lastCheckin && (
                      <p className="text-xs text-[var(--text-muted)]">
                        · Last submitted: {new Date(lastCheckin.submitted_at).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}
                        {lastCheckin.flagged_count > 0 && (
                          <span className="text-red-400 ml-1">({lastCheckin.flagged_count} flagged)</span>
                        )}
                      </p>
                    )}
                    {s.submission_count > 0 && (
                      <p className="text-xs text-[var(--text-muted)]">
                        · {s.submission_count} total
                      </p>
                    )}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleToggle(s)}
                      title={s.is_active ? 'Pause schedule' : 'Resume schedule'}
                      className="p-1.5 text-[var(--text-muted)] hover:text-amber-400 transition-colors"
                    >
                      <RefreshCw size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(s)}
                      className="p-1.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showAdd && (
        <AddScheduleModal
          vehicleId={vehicleId}
          templates={activeTemplates}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function GarageVehicleDetailPage() {
  const { id }   = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const { data: vehicle, isLoading, error } = useVehicle(id)
  const { data: jobs    = [], isLoading: jobsLoading    } = useJobs({ vehicleId: id })
  const { data: issues  = [], isLoading: issuesLoading  } = useIssues({ vehicleId: id })
  const updateVehicle = useUpdateVehicle()
  const updateMileage = useUpdateMileage()

  const isAdmin = ['garage_admin', 'platform_admin'].includes(user?.role)

  const [editing,    setEditing]    = useState(false)
  const [form,       setForm]       = useState(null)
  const [mileageVal, setMileageVal] = useState('')
  const [editMil,    setEditMil]    = useState(false)

  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const startEdit = () => {
    setForm({
      make:         vehicle.make                      || '',
      model:        vehicle.model                     || '',
      year:         vehicle.year                      || '',
      colour:       vehicle.colour                    || '',
      vin:          vehicle.vin                       || '',
      engineSize:   vehicle.engine_size               || '',
      fuelType:     vehicle.fuel_type                 || '',
      transmission: vehicle.transmission              || '',
      notes:        vehicle.notes                     || '',
      motExpiry:    vehicle.mot_expiry?.slice(0, 10)  || '',
    })
    setEditing(true)
  }

  const handleSave = async () => {
    try {
      await updateVehicle.mutateAsync({ id, ...form, year: form.year ? parseInt(form.year) : null, motExpiry: form.motExpiry || null })
      toast.success('Vehicle updated')
      setEditing(false)
    } catch (err) {
      toast.error(err.response?.data?.error || 'Update failed')
    }
  }

  const handleMileageSave = async () => {
    if (!mileageVal) return
    try {
      await updateMileage.mutateAsync({ id, mileage: parseInt(mileageVal) })
      toast.success('Mileage updated')
      setEditMil(false)
      setMileageVal('')
    } catch {
      toast.error('Mileage update failed')
    }
  }

  // ── Loading / error ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse max-w-2xl">
        <div className="h-8 w-32 bg-[var(--bg-surface)] rounded-lg" />
        <div className="h-40 bg-[var(--bg-surface)] rounded-xl" />
        <div className="h-48 bg-[var(--bg-surface)] rounded-xl" />
      </div>
    )
  }

  if (error || !vehicle) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/garage/customers')}
          className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          <ArrowLeft size={16} /> Customer Vehicles
        </button>
        <p className="text-[var(--text-secondary)]">Vehicle not found.</p>
      </div>
    )
  }

  const activeJobs    = jobs.filter(j => !['completed', 'cancelled'].includes(j.status))
  const completedJobs = jobs
    .filter(j => j.status === 'completed')
    .sort((a, b) => new Date(b.date_completed || b.created_at) - new Date(a.date_completed || a.created_at))
  const openIssues = issues.filter(i =>
    ['open', 'deferred'].includes(i.status) &&
    (!i.linked_job_status || i.linked_job_status === 'cancelled')
  )

  return (
    <div className="space-y-6 pb-12 max-w-2xl">
      {/* Back */}
      <button
        onClick={() => navigate('/garage/customers')}
        className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
      >
        <ArrowLeft size={16} />
        Customer Vehicles
      </button>

      {/* Header */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Plate registration={vehicle.registration} size="lg" />
            <div>
              <h1 className="font-display text-xl font-bold text-[var(--text-primary)]">
                {vehicle.make} {vehicle.model}
              </h1>
              {vehicle.year && (
                <p className="text-sm text-[var(--text-muted)]">{vehicle.year}</p>
              )}
            </div>
          </div>

          {vehicle.fuel_type && (
            <span className={clsx('shrink-0 px-2 py-0.5 rounded text-xs font-medium capitalize',
              FUEL_STYLES[vehicle.fuel_type] || 'bg-gray-500/15 text-gray-400')}>
              {vehicle.fuel_type}
            </span>
          )}
        </div>

        {/* Active jobs summary */}
        {activeJobs.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[var(--border)]">
            <p className="text-xs text-amber-400">
              {activeJobs.length} active {activeJobs.length === 1 ? 'job' : 'jobs'} in progress
            </p>
          </div>
        )}
      </div>

      {/* Vehicle details */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-[var(--text-primary)]">Vehicle Details</h3>
          {isAdmin && !editing && (
            <button
              onClick={startEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-overlay)]
                         hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] text-xs
                         font-medium rounded-lg transition-colors"
            >
              <Edit2 size={13} /> Edit
            </button>
          )}
          {editing && (
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={updateVehicle.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700
                           disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors">
                <Check size={13} /> Save
              </button>
              <button onClick={() => setEditing(false)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--bg-overlay)]
                           hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] text-xs
                           font-medium rounded-lg transition-colors">
                <X size={13} /> Cancel
              </button>
            </div>
          )}
        </div>

        {editing ? (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Make',         field: 'make',         type: 'text'   },
              { label: 'Model',        field: 'model',        type: 'text'   },
              { label: 'Year',         field: 'year',         type: 'number' },
              { label: 'Colour',       field: 'colour',       type: 'text'   },
              { label: 'Engine size',  field: 'engineSize',   type: 'text',  placeholder: '2.0L' },
              { label: 'VIN',          field: 'vin',          type: 'text'   },
              { label: 'MOT Expiry',   field: 'motExpiry',    type: 'date'   },
            ].map(({ label, field, type, placeholder }) => (
              <div key={field}>
                <label className="block text-xs text-[var(--text-muted)] mb-1">{label}</label>
                <input type={type} value={form[field]} onChange={set(field)} placeholder={placeholder}
                  className={inputCls} />
              </div>
            ))}
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Fuel type</label>
              <select value={form.fuelType} onChange={set('fuelType')} className={inputCls}>
                <option value="">—</option>
                {FUEL_OPTIONS.map(f => (
                  <option key={f} value={f}>{f.charAt(0).toUpperCase() + f.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--text-muted)] mb-1">Transmission</label>
              <select value={form.transmission} onChange={set('transmission')} className={inputCls}>
                <option value="">—</option>
                {TRANS_OPTIONS.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-[var(--text-muted)] mb-1">Notes</label>
              <textarea rows={2} value={form.notes} onChange={set('notes')}
                className={clsx(inputCls, 'resize-none')} />
            </div>
          </div>
        ) : (
          <>
            <DetailRow label="Registration" value={vehicle.registration} />
            <DetailRow label="Make"         value={vehicle.make} />
            <DetailRow label="Model"        value={vehicle.model} />
            <DetailRow label="Year"         value={vehicle.year} />
            <DetailRow label="Colour"       value={vehicle.colour} />
            <DetailRow label="Engine size"  value={vehicle.engine_size} />
            <DetailRow label="Transmission" value={vehicle.transmission
              ? vehicle.transmission.charAt(0).toUpperCase() + vehicle.transmission.slice(1) : null} />
            <DetailRow label="VIN"          value={vehicle.vin} />
            <DetailRow label="MOT Expiry"   value={vehicle.mot_expiry
              ? new Date(vehicle.mot_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
              : null} />

            {/* Mileage */}
            <div className="flex items-center justify-between gap-4 py-2.5 border-b border-[var(--border)]">
              <span className="text-xs text-[var(--text-muted)]">Mileage</span>
              {editMil ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={mileageVal}
                    onChange={e => setMileageVal(e.target.value)}
                    placeholder="e.g. 45000"
                    className="w-32 px-2 py-1 text-xs bg-[var(--bg-base)] border border-[var(--border)]
                               rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                  />
                  <button onClick={handleMileageSave} disabled={updateMileage.isPending}
                    className="p-1 text-indigo-400 hover:text-indigo-300 disabled:opacity-50">
                    <Check size={14} />
                  </button>
                  <button onClick={() => setEditMil(false)} className="p-1 text-[var(--text-muted)]">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[var(--text-primary)]">
                    {vehicle.current_mileage
                      ? `${Number(vehicle.current_mileage).toLocaleString()} miles`
                      : '—'}
                  </span>
                  {isAdmin && (
                    <button onClick={() => { setMileageVal(vehicle.current_mileage || ''); setEditMil(true) }}
                      className="text-xs text-indigo-400 hover:text-indigo-300">
                      <Gauge size={13} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {vehicle.notes && (
              <div className="pt-3">
                <p className="text-xs text-[var(--text-muted)] mb-1">Notes</p>
                <p className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">
                  {vehicle.notes}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* MOT expiry warning */}
      {(() => {
        if (!vehicle.mot_expiry) return null
        const days = Math.ceil((new Date(vehicle.mot_expiry) - new Date()) / 86400000)
        if (days > 60) return null
        const urgent = days <= 30
        return (
          <div className={clsx('flex items-start gap-3 px-4 py-4 rounded-xl border',
            urgent
              ? 'bg-red-500/10 border-red-500/30 text-red-400'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-400')}>
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm">
                {days <= 0 ? 'MOT has expired' : `MOT expires in ${days} day${days === 1 ? '' : 's'}`}
              </p>
              <p className={clsx('text-xs mt-0.5', urgent ? 'text-red-400/80' : 'text-amber-400/80')}>
                {new Date(vehicle.mot_expiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                {days > 0 && ' — a pre-MOT inspection job has been raised automatically.'}
              </p>
            </div>
          </div>
        )
      })()}

      {/* Owners */}
      <OwnersPanel vehicleId={id} ownership={vehicle.ownership} isAdmin={isAdmin} />

      {/* Jobs */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-medium text-[var(--text-primary)]">
            <span className="flex items-center gap-2">
              <Wrench size={15} className="text-[var(--text-muted)]" />
              Active Jobs
              {!jobsLoading && activeJobs.length > 0 && (
                <span className="text-[var(--text-muted)] font-normal text-sm">
                  ({activeJobs.length})
                </span>
              )}
            </span>
          </h3>
          <button
            onClick={() => navigate(`/garage/jobs?vehicleId=${id}`)}
            className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
          >
            New job →
          </button>
        </div>

        {jobsLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : activeJobs.length === 0 ? (
          <p className="px-5 py-4 text-sm text-[var(--text-muted)]">No active jobs for this vehicle.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {activeJobs.map(job => (
              <button
                key={job.id}
                onClick={() => navigate(`/garage/jobs/${job.id}`)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-[var(--bg-base)]
                           transition-colors text-left group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                                 transition-colors truncate">
                    {job.title}
                  </p>
                  {job.date_booked && (
                    <p className="text-xs text-[var(--text-muted)]">
                      {new Date(job.date_booked).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={clsx('px-2 py-0.5 rounded text-xs font-medium',
                    JOB_STATUS_STYLES[job.status] || 'bg-gray-500/15 text-gray-400')}>
                    {JOB_STATUS_LABELS[job.status] || job.status}
                  </span>
                  <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Check-in schedules */}
      <CheckinSchedulePanel vehicleId={id} isAdmin={isAdmin} />

      {/* Issues */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-medium text-[var(--text-primary)]">
            <span className="flex items-center gap-2">
              <AlertTriangle size={15} className="text-[var(--text-muted)]" />
              Open Issues
              {!issuesLoading && openIssues.length > 0 && (
                <span className="text-[var(--text-muted)] font-normal text-sm">
                  ({openIssues.length})
                </span>
              )}
            </span>
          </h3>
        </div>

        {issuesLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : openIssues.length === 0 ? (
          <p className="px-5 py-4 text-sm text-[var(--text-muted)]">No open issues for this vehicle.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {openIssues.map(issue => (
              <button
                key={issue.id}
                onClick={() => navigate(`/issues/${issue.id}`)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-[var(--bg-base)]
                           transition-colors text-left group"
              >
                <div className={clsx(
                  'w-2 h-2 rounded-full shrink-0',
                  SEVERITY_DOT[issue.severity] || 'bg-gray-400'
                )} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                                 transition-colors truncate">
                    {issue.title}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {new Date(issue.date_identified).toLocaleDateString('en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={clsx('px-2 py-0.5 rounded text-xs font-medium',
                    ISSUE_STATUS_STYLES[issue.status] || 'bg-gray-500/15 text-gray-400')}>
                    {ISSUE_STATUS_LABELS[issue.status] || issue.status}
                  </span>
                  <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* History — completed jobs */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
          <h3 className="font-medium text-[var(--text-primary)]">
            <span className="flex items-center gap-2">
              <History size={15} className="text-[var(--text-muted)]" />
              History
              {!jobsLoading && completedJobs.length > 0 && (
                <span className="text-[var(--text-muted)] font-normal text-sm">
                  ({completedJobs.length})
                </span>
              )}
            </span>
          </h3>
        </div>

        {jobsLoading ? (
          <div className="p-4 space-y-2">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-12 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg animate-pulse" />
            ))}
          </div>
        ) : completedJobs.length === 0 ? (
          <p className="px-5 py-4 text-sm text-[var(--text-muted)]">No completed jobs yet.</p>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {completedJobs.map(job => (
              <button
                key={job.id}
                onClick={() => navigate(`/garage/jobs/${job.id}`)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-[var(--bg-base)]
                           transition-colors text-left group"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[var(--text-primary)] group-hover:text-indigo-400
                                 transition-colors truncate">
                    {job.title}
                  </p>
                  <p className="text-xs text-[var(--text-muted)]">
                    {job.date_completed
                      ? `Completed ${new Date(job.date_completed).toLocaleDateString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric',
                        })}`
                      : job.date_booked
                        ? new Date(job.date_booked).toLocaleDateString('en-GB', {
                            day: 'numeric', month: 'short', year: 'numeric',
                          })
                        : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {job.invoice_total != null && (
                    <p className="text-sm font-semibold text-[var(--text-primary)]">
                      £{parseFloat(job.invoice_total).toFixed(2)}
                    </p>
                  )}
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/15 text-green-400">
                    Completed
                  </span>
                  <ChevronRight size={14} className="text-[var(--text-muted)] group-hover:text-indigo-400" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
